import {DocumentContext, DocumentEntityType, PartyRole} from '@sollapay/enums';
import {Button, Typography, useScrollToTopOnError} from '@sollapay/ui';
import {useEffect} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

import {ErrorAlertMessage} from '@/components';
import {toBeneficiarySnapshots, toBuyerBeneficiarySnapshots} from '@/features/buyers/utils';
import {useViewDocumentMutation} from '@/features/documents/hooks';
import {openDocumentPreview} from '@/features/documents/utils';
import {useCompliancePackageQuery} from '@/features/trust-accounts/activation/hooks';
import {EMPTY_SUPPLEMENTARY_FORM_VALUES} from '@/features/trust-accounts/activation/packages/compliance/utils';
import {
  FatcaCrsSupplementarySection,
  SupplementaryBeneficiaryTables,
  type SupplementaryDeclarationValues
} from '@/features/trust-accounts/fatca-crs';
import {useBeneficiariesQuery} from '@/features/trust-accounts/hooks';
import {useCurrentTrustAccount, useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {BuyerFatcaCrsSupplementarySkeleton} from './BuyerFatcaCrsSupplementarySkeleton';

import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {FatcaCrsSupplementaryPayload} from '@sollapay/types';
import type {FC} from 'react';
import type {UseFormReturn} from 'react-hook-form';

const PREFIX = 'activation.packages.compliance.supplementary';
export interface BuyerFatcaCrsSupplementaryTabContentProps {
  trustAccountId: string;
  buyerForm: UseFormReturn<CreateBuyerFormValues>;
  onSupplementaryValuesChange: (values: FatcaCrsSupplementaryPayload | null) => void;
  saveError?: unknown;
}

export const BuyerFatcaCrsSupplementaryTabContent: FC<
  BuyerFatcaCrsSupplementaryTabContentProps
> = ({trustAccountId, buyerForm, onSupplementaryValuesChange, saveError}) => {
  const {t} = useTranslation('trustAccounts');
  useScrollToTopOnError(saveError);

  const complianceQuery = useCompliancePackageQuery(trustAccountId);
  const mainDeclaration = complianceQuery.data?.fatcaCrs.latest ?? null;

  const beneficiariesQuery = useBeneficiariesQuery(trustAccountId);

  const {name: projectName, lawyerName: rawLawyerName} = useCurrentTrustAccount();
  const lawyerName = rawLawyerName ?? undefined;

  const viewDocMutation = useViewDocumentMutation(
    DocumentEntityType.TRUST_ACCOUNT,
    trustAccountId,
    DocumentContext.COMPLIANCE
  );

  const handleViewOriginal = async () => {
    if (!mainDeclaration?.documentId) return;
    const url = await viewDocMutation.mutateAsync(mainDeclaration.documentId);
    await openDocumentPreview(url);
  };

  const supplementaryForm = useForm<SupplementaryDeclarationValues>({
    defaultValues: EMPTY_SUPPLEMENTARY_FORM_VALUES
  });
  const {control, setValue} = supplementaryForm;

  // See toBuyerBeneficiarySnapshots for why every buyer shares one role.
  const strategy = useTrustTypeStrategy();
  const buyerRole = strategy.getBuyerPartyRoles()[0] ?? PartyRole.BUYER;

  const liveBuyerValues = useWatch({control: buyerForm.control}) as CreateBuyerFormValues;
  const addedBeneficiaries = toBuyerBeneficiarySnapshots(liveBuyerValues, buyerRole);
  const existingBeneficiaries = toBeneficiarySnapshots(beneficiariesQuery.data ?? []);

  const supplementaryValues = useWatch({control});

  useEffect(() => {
    if (mainDeclaration) {
      onSupplementaryValuesChange({
        declarationValues: supplementaryValues.declarationValues as Record<string, boolean>
      });
    } else {
      onSupplementaryValuesChange(null);
    }
  }, [supplementaryValues.declarationValues, mainDeclaration?.id, onSupplementaryValuesChange]);

  if (complianceQuery.isLoading || beneficiariesQuery.isLoading) {
    return <BuyerFatcaCrsSupplementarySkeleton />;
  }
  return (
    <>
      {saveError != null && <ErrorAlertMessage error={saveError} />}
      <Typography as="h2" size="xl" weight="semibold" className="whitespace-normal">
        {t(`${PREFIX}.title`, {projectName})}
      </Typography>

      <Typography size="sm" color="secondary" className="whitespace-normal">
        <Trans
          i18nKey={`${PREFIX}.subtitle`}
          ns="trustAccounts"
          components={{
            viewLink: (
              <Button
                variant="linkColor"
                size="sm"
                disabled={!mainDeclaration?.documentId || viewDocMutation.isPending}
                onClick={handleViewOriginal}
                className="h-auto p-0 font-medium"
              />
            )
          }}
        />
      </Typography>

      <FatcaCrsSupplementarySection
        mainDeclaration={mainDeclaration}
        lawyerName={lawyerName}
        control={control}
        setValue={setValue}
      >
        <SupplementaryBeneficiaryTables
          addedBeneficiaries={addedBeneficiaries}
          existingBeneficiaries={existingBeneficiaries}
        />
      </FatcaCrsSupplementarySection>
    </>
  );
};
